// src/services/nodeApi.js

import * as api from './api';



/**
 * Get node details by reference code
 * @param {string} referenceCode - Node reference code
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - Wallet type
 * @returns {Promise<Object>} Response with node details
 */
export const getNodeDetails = async (referenceCode, walletAddress, blockchainNetworkId, walletType) => {
  try {
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Fetch node details
    const response = await fetch('https://api.aeronyx.network/api/nodes/details/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        reference_code: referenceCode,
        wallet_address: walletAddress,
        blockchain_network_id: blockchainNetworkId,
        signature: signature,
        message: messageResponse.message,
        wallet_type: walletType
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch node details');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in getNodeDetails:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch node details'
    };
  }
};

/**
 * Fetch all nodes owned by the user
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - Wallet type
 * @returns {Promise<Object>} Response with user's nodes data
 */
export const getUserNodes = async (walletAddress, blockchainNetworkId, walletType) => {
  try {
    // Get signature message
    const messageResponse = await fetch('https://api.aeronyx.network/api/generate-signature-message/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: blockchainNetworkId
      }),
    });
    
    if (!messageResponse.ok) {
      throw new Error('Failed to generate signature message');
    }
    
    const messageData = await messageResponse.json();
    
    if (!messageData.success || !messageData.data || !messageData.data.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageData.data.message
    );
    
    // Fetch user's nodes using the active nodes endpoint (which matches your backend)
    const response = await fetch('https://api.aeronyx.network/api/nodes/active/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        blockchain_network_id: blockchainNetworkId,
        signature: signature,
        message: messageData.data.message,
        wallet_type: walletType
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch nodes: ${response.status} ${response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch user nodes');
    }
    
    // The nodes are in result.data.nodes according to the get_active_nodes function in backend
    return {
      success: true,
      nodes: result.data?.nodes || result.data?.nodesByTier || []
    };
  } catch (error) {
    console.error('Error in getUserNodes:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch user nodes',
      nodes: []
    };
  }
};



/**
 * Get node downline information
 * @param {string} nodeReferenceCode - Node reference code
 * @param {string} walletAddress - User's wallet address
 * @param {number} blockchainNetworkId - Blockchain network ID
 * @param {string} walletType - Wallet type
 * @param {boolean} includeIndirect - Whether to include indirect relationships
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @returns {Promise<Object>} Response with downline data
 */
export const getNodeDownline = async (
  nodeReferenceCode, 
  walletAddress,
  blockchainNetworkId,
  walletType,
  includeIndirect = true,
  page = 1,
  pageSize = 20
) => {
  try {
    // Get signature message
    const messageResponse = await api.generateSignatureMessage(
      walletAddress,
      blockchainNetworkId
    );
    
    if (!messageResponse || !messageResponse.message) {
      throw new Error('Invalid signature message response');
    }
    
    // Sign message
    const signature = await api.signMessage(
      walletType,
      walletAddress,
      messageResponse.message
    );
    
    // Fetch downline data
    const response = await fetch('https://api.aeronyx.network/api/nodes/downline/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        wallet_address: walletAddress,
        node_reference_code: nodeReferenceCode,
        blockchain_network_id: blockchainNetworkId,
        signature: signature,
        message: messageResponse.message,
        wallet_type: walletType,
        include_indirect: includeIndirect,
        page: page,
        page_size: pageSize
      }),
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || 'Failed to fetch downline data');
    }
    
    return {
      success: true,
      data: result.data
    };
  } catch (error) {
    console.error('Error in getNodeDownline:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch downline data'
    };
  }
};

export default {
  getUserNodes,
  getNodeDetails,
  getNodeDownline
};
